import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { X, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [showCodeInput, setShowCodeInput] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navLinks = [
    { path: "/", label: "Ana Sayfa" },
    { path: "/hizmetler", label: "Hizmetlerimiz" },
    { path: "/teknoloji", label: "Teknoloji" },
    { path: "/iletisim", label: "İletişim" },
  ];

  const isActive = (path) => location.pathname === path;

  // Check admin access count
  const getAdminAccessCount = () => {
    return parseInt(localStorage.getItem("admin_access_count") || "0");
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: codeInput })
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem("admin_token", data.access_token);
        localStorage.setItem("admin_access_count", "10");
        setShowCodeInput(false);
        setCodeInput("");
        navigate("/admin/dashboard");
      }
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  // Decrease access count on page load if admin is logged in
  useEffect(() => {
    const count = getAdminAccessCount();
    if (count > 0 && location.pathname !== "/admin/dashboard") {
      localStorage.setItem("admin_access_count", String(count - 1));
    }
    if (count <= 0) {
      localStorage.removeItem("admin_token");
    }
  }, [location.pathname]);

  return (
    <>
      {/* Main Header */}
      <header className="bg-white sticky top-0 z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Code Input Button */}
            <button
              className="p-2 text-navy hover:text-cyan-500 transition-colors"
              onClick={() => setShowCodeInput(!showCodeInput)}
              data-testid="code-toggle"
            >
              <Search className="w-6 h-6" />
            </button>

            {/* Logo - Center with Text */}
            <Link to="/" className="flex items-center gap-1 sm:gap-3" data-testid="header-logo">
              <span className="font-heading font-bold text-base sm:text-xl bg-gradient-to-r from-[#0A192F] to-cyan-600 bg-clip-text text-transparent whitespace-nowrap">
                Hisar
              </span>
              <img 
                src="https://customer-assets.emergentagent.com/job_dental-lab-pro-1/artifacts/996kini1_Gemini_Generated_Image_gsnmeggsnmeggsnm.png" 
                alt="Hisar Dental Lab" 
                className="h-10 sm:h-14 w-auto"
              />
              <span translate="no" className="font-heading font-bold text-base sm:text-xl bg-gradient-to-r from-cyan-600 to-[#0A192F] bg-clip-text text-transparent whitespace-nowrap notranslate">
                Dental
              </span>
            </Link>

            {/* Teklif Al Button */}
            <Link
              to="/iletisim#iletisim-formu"
              className="bg-navy text-white hover:bg-teal rounded-full px-5 py-2.5 transition-all duration-300 font-medium text-sm"
              data-testid="header-cta"
            >
              İletişim Kur
            </Link>
          </div>
        </div>

        {/* Code Input Panel */}
        <AnimatePresence>
          {showCodeInput && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="border-t border-slate-100 bg-slate-50"
            >
              <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
                <form onSubmit={handleCodeSubmit} className="flex gap-3 max-w-md">
                  <input
                    type="password"
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value)}
                    placeholder="Yönetici kodu..."
                    className="flex-1 bg-white border border-slate-200 rounded-lg px-4 py-2 outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                    data-testid="admin-code-input"
                  />
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-[#0A192F] to-cyan-500 text-white px-6 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity"
                    data-testid="admin-code-submit"
                  >
                    Giriş
                  </button>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation Menu */}
        <nav className="border-t border-slate-100 hidden md:block">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-center gap-8 h-12">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.path.slice(1) || 'home'}`}
                  className={`text-sm font-medium transition-colors relative ${
                    isActive(link.path)
                      ? "text-cyan-500"
                      : "text-slate-600 hover:text-navy"
                  }`}
                >
                  {link.label}
                  {isActive(link.path) && (
                    <motion.div
                      layoutId="activeNav"
                      className="absolute -bottom-[13px] left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-blue-500"
                    />
                  )}
                </Link>
              ))}
            </div>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <nav className="border-t border-slate-100 md:hidden overflow-x-auto">
          <div className="flex items-center gap-4 px-4 h-12">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive(link.path)
                    ? "text-cyan-500"
                    : "text-slate-600"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </nav>
      </header>
    </>
  );
};

export default Header;
